import { Button, Card, Col, Row, Select, Table, TableColumnsType, Tabs } from 'antd';
import { useEffect, useMemo, useState } from 'react'
import { NavLink } from 'react-router-dom';
import StudentService from '../services/StudentService';
import { formatDate } from '../utils';
import CourseService from '../services/CourseService';

interface PurchasedCourse {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
  courseId: number;
  courseName: string;
  price: number;
  commission: number;
  createdAt: string;
}

const Students = () => {
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [packages, setPackages] = useState<PurchasedCourse[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<number | undefined>();
  const [accountId, setAccountId] = useState<any>(0);

  useEffect(() => {
    getStudents()
    getPurchasedCourseList()
  }, []);

  const getPurchasedCourseList = () => {
    CourseService.getPurchasedCourseList()
      .then((response: any) => {
        setPackages(response.data);
      })
      .catch((e: Error) => {
        console.log(e);
        setLoading(false);
      });
  };

  const packageOptions = useMemo(() => {
    const uniquePackages = new Map<number, string>();
    packages.forEach(({ courseId, courseName }) => uniquePackages.set(courseId, courseName));

    return Array.from(uniquePackages, ([value, label]) => ({ value, label }));
  }, [packages]);

  const filteredPackages = selectedPackageId === undefined
    ? packages
    : packages.filter(({ courseId }) => courseId === selectedPackageId);

  const totalCommission = filteredPackages.reduce((total, item) => total + Number(item.commission), 0);
  const totalSales = filteredPackages.reduce((total, item) => total + Number(item.price), 0);


  const loginByAccountId = (id: any) => {
    setAccountId(id);
    setLoginLoading(true);
    StudentService.loginByAccountId(id)
      .then((response: any) => {
        const accessToken = response.data.tokenId;
        // Redirect using the token
        
        // Copy the token to clipboard
        window.open(`${process.env.REACT_APP_BASE_URL}/login-by-token/${accessToken}`, '_blank');
  
        setLoginLoading(false);
        setAccountId(null);
      })
      .catch((e: Error) => {
        setLoginLoading(false);
        console.error('Login failed', e);
      });
  }
  

  const getStudents = () => {
    setLoading(true);
    StudentService.getAll()
      .then((response: any) => {
        const data = response.data;
        setStudents(data);
        setLoading(false);
      })
      .catch((e: Error) => {
        console.log(e);
        setLoading(false);
      });
  };

  const PackagesColumns: TableColumnsType<PurchasedCourse> = [
    {
      title: 'İsim',
      dataIndex: 'firstName',
      key: 'firstName',
    },
    {
      title: 'Soyisim',
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: 'Paket',
      dataIndex: 'courseName',
      key: 'courseName',
    },
    {
      title: 'Paket Ücreti',
      dataIndex: 'price',
      render: (price) => <b>{price} ₺</b>,
    },
    {
      title: 'Komisyon',
      dataIndex: 'commission',
      render: (commission) => <b>{commission} ₺</b>,
    },
    {
      title: 'E-Posta',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'İşlem Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) => <>{formatDate(createdAt)}</>,
      sorter: (a, b) => {
        // Parse the ISO 8601 date strings into timestamps for numerical sorting
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      },
      defaultSortOrder: 'descend', // Sets default sort order to descending
    },
  ];

  const StudentsColumns: TableColumnsType<any> = [
    {
      title: 'İsim',
      dataIndex: 'firstName',
      key: 'firstName',
    },
    {
      title: 'Soyisim',
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: 'E-Posta',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Kayıt Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) => (
        <>
          {formatDate(createdAt)}
        </>
      ),
    },
    {
      title: 'Son Giriş Tarihi',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      render: (lastLoginAt: string | null) => lastLoginAt ? formatDate(lastLoginAt) : 'Giriş kaydı yok',
      sorter: (a, b) => (a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0)
        - (b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0),
    },
    {
      key: 'id',
      dataIndex: 'id',
      title: '',
      render: (id) => (
        <>
          <Row gutter={[24, 24]}>
            <Col>
              <NavLink to={`/students/${id}`}>
                <Button type="primary" block>
                  Detay
                </Button>
              </NavLink>

            </Col>
            <Col>
              <Button type="primary" loading={(loginLoading && accountId == id)} disabled={(loginLoading && accountId != id)} onClick={() => loginByAccountId(id)} block>
                Uygulama Giriş
              </Button>
            </Col>
          </Row>
        </>
      ),
    },
  ];

  return (
    <Tabs
      defaultActiveKey="1"
      items={[
        {
          label: 'Öğrenci Listesi',
          key: '1',
          children: <Card title="Öğrenci Listesi" extra={`Toplam Öğrenci: ${students.length}`}>
            <Table
              rowKey="id"
              pagination={{ pageSize: 100 }}
              loading={loading}
              dataSource={students}
              columns={StudentsColumns}
              scroll={{ x: 'max-content' }}
            />
          </Card>,
        },
        {
          label: 'Paket Satış',
          key: '2',
          children: <Card
            title="Paket Satış"
            extra={<span data-testid="package-sales-summary">Toplam Satış: <b>{filteredPackages.length}</b> | Toplam Ciro: <b>{totalSales}</b> ₺ | Toplam Komisyon: <b>{totalCommission}</b> ₺</span>}
          >
            <Row align="middle" data-testid="package-filter-row" gutter={12}>
              <Col flex="none">
                <strong data-testid="package-filter-label">Paket</strong>
              </Col>
              <Col flex="1">
                <Select
                  allowClear
                  aria-label="Paket filtresi"
                  optionFilterProp="label"
                  options={packageOptions}
                  placeholder="Paket seçin"
                  showSearch
                  style={{ width: '100%' }}
                  value={selectedPackageId}
                  onChange={setSelectedPackageId}
                />
              </Col>
            </Row>
            <Table
              rowKey="id"
              pagination={{ pageSize: 100 }}
              loading={loading}
              dataSource={filteredPackages}
              columns={PackagesColumns}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        }
      ]}
    />

  )
}

export default Students
